// ============================================================
// Reusable animation primitives — Framer Motion
// All respect prefers-reduced-motion via useReducedMotion()
// Wrap any section/element to opt-in to motion behaviour.
// ============================================================

import { useRef, useState, useEffect, useCallback } from 'react';
import { motion, useReducedMotion, useScroll, useTransform, useSpring } from 'framer-motion';

// === RevealOnScroll ============================================
// Fade-in + Y translate when the element enters the viewport.
// Default: opacity 0→1, y 18→0, duration 0.55s, ease editorial cubic.
//
// Usage:
//   <RevealOnScroll>
//     <section>...</section>
//   </RevealOnScroll>
//
//   <RevealOnScroll delay={0.1}>...</RevealOnScroll>
//   <RevealOnScroll y={32} once={false}>...</RevealOnScroll>
export function RevealOnScroll({
  children,
  delay = 0,
  y = 18, // eslint-disable-line no-unused-vars -- conservé pour compat d'API (transform retiré, cf. style)
  duration = 0.55,
  once = true, // eslint-disable-line no-unused-vars -- toujours "once" ; gardé pour compat d'API
  className = '',
  as = 'div',
  ...rest
}) {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  const Tag = as || 'div';

  // 2026-06-14 (fix Jade « animation au scroll pas optimisée ») — on n'utilise
  // PLUS `whileInView` de framer-motion ni IntersectionObserver ici. Les deux
  // laissaient des sections bloquées à `opacity:0` (contenu majeur invisible
  // sur la home) sous React StrictMode et au scroll rapide. On utilise un
  // simple listener `scroll`/`resize` qui teste getBoundingClientRect : c'est
  // 100 % fiable (aucune abstraction async), ça se vérifie au montage (section
  // déjà visible → révélée tout de suite) et le listener s'auto-retire dès que
  // l'élément est révélé (donc zéro coût une fois affiché). Transition CSS =
  // plus léger que l'animation JS framer → meilleure perf au scroll.
  useEffect(() => {
    if (reduce) { setShown(true); return undefined; }
    const el = ref.current;
    if (!el) return undefined;
    let done = false;
    const cleanups = [];
    const reveal = () => {
      if (done) return;
      done = true;
      setShown(true);
      cleanups.forEach((fn) => fn());
      cleanups.length = 0;
    };
    const inView = () => {
      const r = el.getBoundingClientRect();
      return r.top < window.innerHeight * 0.95 && r.bottom > 0;
    };
    // Déjà visible au montage ?
    if (inView()) { reveal(); return undefined; }
    // TRIPLE REDONDANCE pour ne JAMAIS laisser une section bloquée invisible
    // (le scroll seul ratait parfois une section sous StrictMode / scroll
    // rapide). Le 1er des 3 qui voit l'élément entrer le révèle :
    //   1) IntersectionObserver (principal, fiable)
    //   2) listener scroll/resize (filet de sécurité)
    //   3) check au montage (ci-dessus)
    if (typeof IntersectionObserver !== 'undefined') {
      const io = new IntersectionObserver(
        (entries) => { if (entries.some((e) => e.isIntersecting)) reveal(); },
        { rootMargin: '0px 0px -5% 0px' },
      );
      io.observe(el);
      cleanups.push(() => io.disconnect());
    }
    const onScroll = () => { if (inView()) reveal(); };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    cleanups.push(() => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    });
    return () => { cleanups.forEach((fn) => fn()); };
  }, [reduce]);

  if (reduce) {
    return <Tag className={className} {...rest}>{children}</Tag>;
  }

  return (
    <Tag
      ref={ref}
      className={className}
      style={{
        // Opacity SEULE — pas de `transform`. Un transform (même le translateY
        // du reveal) sur la section crée un bloc conteneur qui PIÈGE les
        // descendants `position: fixed` : le pop-up d'aperçu d'article (fixed)
        // se positionnait alors par rapport à la section au lieu du viewport et
        // s'affichait au mauvais endroit. `will-change: opacity` ne crée PAS de
        // bloc conteneur (contrairement à transform/filter). Fix Jade 2026-06-14.
        opacity: shown ? 1 : 0,
        transition: `opacity ${duration}s cubic-bezier(0.22, 1, 0.36, 1) ${delay}s`,
        willChange: shown ? 'auto' : 'opacity',
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

// === RevealStagger ============================================
// Wraps a list of children — each child reveals with stagger delay.
//
// Usage:
//   <RevealStagger>
//     <Card />
//     <Card />
//     <Card />
//   </RevealStagger>
export function RevealStagger({ children, stagger = 0.08, delay = 0, className = '', ...rest }) {
  const reduce = useReducedMotion();

  if (reduce) {
    return <div className={className} {...rest}>{children}</div>;
  }

  return (
    <motion.div
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: '-80px' }}
      variants={{
        show: { transition: { staggerChildren: stagger, delayChildren: delay } },
      }}
      className={className}
      {...rest}
    >
      {Array.isArray(children) ? children.map((child, i) => (
        <motion.div
          key={i}
          variants={{
            hidden: { opacity: 0, y: 18 },
            show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
          }}
        >
          {child}
        </motion.div>
      )) : (
        <motion.div
          variants={{
            hidden: { opacity: 0, y: 18 },
            show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
          }}
        >
          {children}
        </motion.div>
      )}
    </motion.div>
  );
}

// === TextReveal ===============================================
// Word-by-word fade-in for headlines. Splits on whitespace,
// preserves React children only if string.
//
// Usage:
//   <TextReveal className="font-serif text-5xl">
//     Le guide écrit par celles qui l'ont fait.
//   </TextReveal>
//
//   <TextReveal as="h1" stagger={0.04} delay={0.1}>...</TextReveal>
export function TextReveal({
  children,
  stagger = 0.05,
  delay = 0,
  duration = 0.5,
  className = '',
  as: Tag = 'span',
  ...rest
}) {
  const reduce = useReducedMotion();

  if (reduce || typeof children !== 'string') {
    return <Tag className={className} {...rest}>{children}</Tag>;
  }

  const words = children.split(/(\s+)/); // keep whitespace tokens

  return (
    <Tag className={className} aria-label={children} {...rest}>
      <motion.span
        initial="hidden"
        animate="show"
        variants={{
          show: { transition: { staggerChildren: stagger, delayChildren: delay } },
        }}
        aria-hidden="true"
        style={{ display: 'inline' }}
      >
        {words.map((word, i) =>
          /^\s+$/.test(word) ? (
            <span key={i}>{word}</span>
          ) : (
            <motion.span
              key={i}
              style={{ display: 'inline-block', willChange: 'transform, opacity' }}
              variants={{
                hidden: { opacity: 0, y: 12 },
                show: {
                  opacity: 1,
                  y: 0,
                  transition: { duration, ease: [0.22, 1, 0.36, 1] },
                },
              }}
            >
              {word}
            </motion.span>
          ),
        )}
      </motion.span>
    </Tag>
  );
}

// === MagneticButton ===========================================
// Wrap any button/link so it subtly attracts the cursor.
// Effect kicks in only when cursor is within 80px of element center.
//
// Usage:
//   <MagneticButton>
//     <button className="btn-primary">Acheter</button>
//   </MagneticButton>
export function MagneticButton({
  children,
  strength = 0.25,        // 0-1, how strongly element follows cursor
  radius = 80,            // px range at which effect kicks in
  className = '',
  ...rest
}) {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const [pos, setPos] = useState({ x: 0, y: 0 });

  const handleMove = useCallback((e) => {
    if (reduce || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = e.clientX - cx;
    const dy = e.clientY - cy;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist > radius) {
      setPos({ x: 0, y: 0 });
      return;
    }
    setPos({ x: dx * strength, y: dy * strength });
  }, [reduce, radius, strength]);

  const handleLeave = useCallback(() => setPos({ x: 0, y: 0 }), []);

  useEffect(() => {
    if (reduce) return undefined;
    window.addEventListener('mousemove', handleMove);
    return () => window.removeEventListener('mousemove', handleMove);
  }, [handleMove, reduce]);

  return (
    <motion.div
      ref={ref}
      animate={{ x: pos.x, y: pos.y }}
      transition={{ type: 'spring', stiffness: 150, damping: 15, mass: 0.3 }}
      onMouseLeave={handleLeave}
      className={`inline-block ${className}`}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

// === CountUp ==================================================
// Animates a number from 0 to `value` when scrolled into view.
// Lightweight — uses Framer Motion's animate() under the hood.
//
// Usage:
//   <CountUp value={47} suffix=" docs" />
//   <CountUp value={28} duration={1.5} format={(n) => n.toFixed(0) + '$'} />
export function CountUp({
  value = 0,
  duration = 1.4,
  format,
  suffix = '',
  prefix = '',
  className = '',
}) {
  const ref = useRef(null);
  const [displayed, setDisplayed] = useState(0);
  const reduce = useReducedMotion();

  useEffect(() => {
    if (reduce) {
      setDisplayed(value);
      return undefined;
    }
    const el = ref.current;
    if (!el) return undefined;

    let frame = 0;
    let raf = 0;
    let started = false;
    const start = performance.now();

    const tick = (now) => {
      const elapsed = (now - start) / 1000;
      const t = Math.min(1, elapsed / duration);
      // Same editorial easing
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplayed(value * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started) {
            started = true;
            raf = requestAnimationFrame(tick);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.4 },
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [value, duration, reduce]);

  const out = format
    ? format(displayed)
    : Math.round(displayed).toLocaleString();

  return (
    <span ref={ref} className={className}>
      {prefix}{out}{suffix}
    </span>
  );
}

// === ParallaxImage ============================================
// Image that scrolls at 0.7x scroll speed (slower) for depth feel.
//
// Usage:
//   <ParallaxImage src="/skyline.jpg" alt="" />
//   <ParallaxImage src="..." speed={0.5} />
export function ParallaxImage({ src, alt = '', speed = 0.7, className = '', ...rest }) {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollY } = useScroll();

  // Smoother movement via spring
  const offset = useTransform(scrollY, (y) => {
    if (!ref.current) return 0;
    const rect = ref.current.getBoundingClientRect();
    const elTop = rect.top + y;
    const relative = y - elTop;
    return relative * (1 - speed);
  });
  const smooth = useSpring(offset, { stiffness: 100, damping: 30, mass: 0.5 });

  if (reduce) {
    return <img ref={ref} src={src} alt={alt} className={className} {...rest} />;
  }

  return (
    <motion.img
      ref={ref}
      src={src}
      alt={alt}
      style={{ y: smooth }}
      className={className}
      {...rest}
    />
  );
}

// === BlurUpImage ==============================================
// LQIP-style blur up — image loads in blurred state, sharpens on load.
//
// Usage:
//   <BlurUpImage src="/photo.jpg" alt="Panama City" width={800} height={600} />
export function BlurUpImage({ src, alt = '', className = '', style = {}, ...rest }) {
  const [loaded, setLoaded] = useState(false);
  const reduce = useReducedMotion();

  return (
    <img
      src={src}
      alt={alt}
      onLoad={() => setLoaded(true)}
      className={className}
      style={{
        ...style,
        filter: loaded || reduce ? 'blur(0)' : 'blur(18px)',
        transform: loaded || reduce ? 'scale(1)' : 'scale(1.04)',
        transition: reduce ? 'none' : 'filter 600ms ease, transform 600ms ease',
        willChange: loaded ? 'auto' : 'filter, transform',
      }}
      {...rest}
    />
  );
}
